import { Component, OnDestroy, OnInit } from '@angular/core';
import { ConfigStateService, DynamicLayoutComponent, ReplaceableComponentsService } from '@abp/ng.core';
import { LoaderBarComponent } from '@abp/ng.theme.shared';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, EMPTY, exhaustMap, filter, from, Subscription, timer } from 'rxjs';
import { SynchronizationManagerService } from './shared/services/synchronization-manager.service';
import { NgxSpinnerComponent } from "ngx-spinner";
import { ToastModule } from 'primeng/toast';
import { SessionStorageConst } from './shared/utils/application.constants';
import { eThemeLeptonXComponents } from '@volosoft/abp.ng.theme.lepton-x';


@Component({
  selector: 'app-root',
  template: `
    <p-toast position="bottom-center" key="bc" />
    <abp-loader-bar />
    <ngx-spinner
      name="component-init-spinner"
      type="ball-clip-rotate">
    </ngx-spinner>
    <abp-dynamic-layout />
  `,
  imports: [LoaderBarComponent, DynamicLayoutComponent, NgxSpinnerComponent, ToastModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly autoSyncIntervalSettingKey =
    'ChemicalEquipmentCalibration.Synchronization.AutoSyncIntervalInMinutes';
  private readonly defaultAutoSyncIntervalMs = 300_000;
  private readonly autoSyncRetryWhileBusyMs = 5_000;
  private autoSyncSubscription?: Subscription;
  private navigationSubscription?: Subscription;
  private lastAuthenticatedEmail?: string;

  constructor(
    private readonly config: ConfigStateService,
    private readonly synchronizationManager: SynchronizationManagerService,
    private readonly router: Router,
    private readonly replaceableComponents: ReplaceableComponentsService
  ) { 
    this.replaceableComponents.add({
      component: null,
      key: eThemeLeptonXComponents.Breadcrumb,
    });
  }

  ngOnInit(): void {    
    this.lastAuthenticatedEmail = this.getAuthenticatedEmail();
    this.startAutoSync();
    this.watchAuthenticationChanges();
  }

  ngOnDestroy(): void {
    this.stopAutoSync();
    this.navigationSubscription?.unsubscribe();
  }

  private startAutoSync(): void {
    if (this.autoSyncSubscription) {
      return;
    }

    void this.scheduleNextAutoSync();
  }

  private async scheduleNextAutoSync(): Promise<void> {
    const isAuthenticated = this.config.getDeep('currentUser.isAuthenticated');
    const email = this.config.getDeep('currentUser.email') as string | undefined;
    const autoSyncIntervalMs = this.getAutoSyncIntervalMs();
    let delay = autoSyncIntervalMs;

    if (isAuthenticated && email) {
      if (this.shouldForceImmediateAutoSync(email)) {
        delay = 0;
      } else if (this.synchronizationManager.isSyncInProgress()) {
        delay = this.autoSyncRetryWhileBusyMs;
      } else {
        const checkpoint = await this.synchronizationManager.getPendingCheckpoint(email);
        const lastSyncAt = await this.synchronizationManager.getLastSyncAt();

        if (checkpoint) {
          delay = 0;
        } else if (!lastSyncAt) {
          delay = 0;
        } else {
          const elapsedTime = Date.now() - new Date(lastSyncAt).getTime();
          delay = Math.max(autoSyncIntervalMs - elapsedTime, 0);
        }
      }
    } else {
      this.clearAutoSyncSessionUser();
    }

    this.autoSyncSubscription = timer(delay)
      .pipe(
        exhaustMap(() => {
          const currentIsAuthenticated = this.config.getDeep('currentUser.isAuthenticated');
          const currentEmail = this.config.getDeep('currentUser.email') as string | undefined;
          if (
            !currentIsAuthenticated ||
            !currentEmail ||
            this.synchronizationManager.isSyncInProgress()
          ) {
            return EMPTY;
          }

          return from(
            this.synchronizationManager.syncAll(currentEmail, {
              showAlreadyInProgressWarning: false,
            })
          ).pipe(
            catchError(error => {
              return EMPTY;
            })
          );
        })
      )
      .subscribe({
        complete: () => {
          this.autoSyncSubscription = undefined;
          void this.scheduleNextAutoSync();
        },
      });
  }

  private stopAutoSync(): void {
    this.autoSyncSubscription?.unsubscribe();
    this.autoSyncSubscription = undefined;
  }

  private watchAuthenticationChanges(): void {
    this.navigationSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.handleAuthenticationStateChange();
      });
  }

  private handleAuthenticationStateChange(): void {
    const authenticatedEmail = this.getAuthenticatedEmail();
    if (authenticatedEmail === this.lastAuthenticatedEmail) {
      return;
    }

    this.lastAuthenticatedEmail = authenticatedEmail;

    if (!authenticatedEmail) {
      this.clearAutoSyncSessionUser();
    }

    this.rescheduleAutoSync();
  }

  private rescheduleAutoSync(): void {
    this.stopAutoSync();
    this.startAutoSync();
  }

  private getAuthenticatedEmail(): string | undefined {
    const isAuthenticated = this.config.getDeep('currentUser.isAuthenticated');
    const email = this.config.getDeep('currentUser.email') as string | undefined;

    if (!isAuthenticated || !email) {
      return undefined;
    }

    return email;
  }

  private shouldForceImmediateAutoSync(email: string): boolean {
    const currentSessionUser = this.getStoredAutoSyncSessionUser();
    if (currentSessionUser === email) {
      return false;
    }

    this.storeAutoSyncSessionUser(email);
    return true;
  }

  private getStoredAutoSyncSessionUser(): string | undefined {
    try {
      return sessionStorage.getItem(SessionStorageConst.autoSyncSessionUser) ?? undefined;
    } catch {
      return undefined;
    }
  }

  private storeAutoSyncSessionUser(email: string): void {
    try {
      sessionStorage.setItem(SessionStorageConst.autoSyncSessionUser, email);
    } catch {
    }
  }

  private clearAutoSyncSessionUser(): void {
    try {
      sessionStorage.removeItem(SessionStorageConst.autoSyncSessionUser);
    } catch {
    }
  }

  private getAutoSyncIntervalMs(): number {
    const settingValue = this.config.getSetting(this.autoSyncIntervalSettingKey);
    const intervalInMinutes = Number(settingValue);

    if (!Number.isFinite(intervalInMinutes) || intervalInMinutes <= 0) {
      return this.defaultAutoSyncIntervalMs;
    }

    return intervalInMinutes * 60_000;
  }
}
